import { useState, useEffect } from 'react';
import axios from 'axios';
import { Play } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultTemples = [
  {
    id: '1',
    name: 'Tirupati Balaji Temple',
    location: 'Tirupati, Andhra Pradesh',
    stream_url: 'https://www.youtube.com/watch?v=1yI4cM67eTI',
    image_url: 'https://images.unsplash.com/photo-1767533427730-cc8791c14558?w=600&h=400&fit=crop',
    description: 'Live darshan from the famous Tirupati temple',
  },
  {
    id: '2',
    name: 'Golden Temple',
    location: 'Amritsar, Punjab',
    stream_url: 'https://www.youtube.com/watch?v=3y4CMLDw8H8',
    image_url: 'https://images.unsplash.com/photo-1769888913192-897f2f097d04?w=600&h=400&fit=crop',
    description: 'Live aarti from Sri Harmandir Sahib',
  },
  {
    id: '3',
    name: 'Varanasi Ganga Aarti',
    location: 'Varanasi, Uttar Pradesh',
    stream_url: 'https://www.youtube.com/watch?v=c9P6XcWBUVo',
    image_url: 'https://images.unsplash.com/photo-1759592370294-30f83cf6a609?w=600&h=400&fit=crop',
    description: 'Evening Ganga Aarti ceremony',
  },
];

export default function LiveAartiPage() {
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchTemples();
  }, []);

  const fetchTemples = async () => {
    try {
      const response = await axios.get(`${API}/temples`);
      if (response.data.length > 0) {
        setTemples(response.data);
      } else {
        setTemples(defaultTemples);
      }
    } catch (error) {
      setTemples(defaultTemples);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading live aartis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="text-center mb-8 peaceful-enter">
        <h2 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Live Aarti
        </h2>
        <p className="text-slate-600">Experience divine ceremonies from sacred temples</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {temples.map((temple, index) => (
          <div
            key={temple.id}
            data-testid={`temple-card-${index}`}
            className="temple-card bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden peaceful-enter"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            {playingVideo === temple.id ? (
              <div className="aspect-video">
                <iframe
                  src={temple.stream_url.replace('watch?v=', 'embed/')}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ) : (
              <div className="relative group cursor-pointer" onClick={() => setPlayingVideo(temple.id)}>
                <img
                  src={temple.image_url}
                  alt={temple.name}
                  className="w-full aspect-video object-cover"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <button
                    data-testid={`play-btn-${index}`}
                    className="w-16 h-16 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all transform group-hover:scale-110"
                  >
                    <Play className="w-8 h-8 text-[#FF9933] ml-1" fill="#FF9933" />
                  </button>
                </div>
              </div>
            )}

            <div className="p-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                {temple.name}
              </h3>
              <p className="text-sm text-slate-600 mb-2">{temple.location}</p>
              {temple.description && (
                <p className="text-sm text-slate-500">{temple.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center bg-orange-50/50 rounded-2xl p-8 border border-orange-100 peaceful-enter">
        <div className="text-3xl text-[#FF9933] mb-3">ॐ</div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Peaceful Viewing
        </h3>
        <p className="text-slate-600">Videos do not autoplay. Click to watch when you're ready.</p>
      </div>
    </div>
  );
}
